import { useLearningStore } from "~/store/learning/learningStore";
import {useLearningAssessmentStore} from "~/store/learning/assessment/learningAssessmentStore";
import { useDialogStore } from "~~/application/store/dialog/dialogStore";
import { CautionType } from "~~/package/types/CautionType";
import { useToastStore } from "~~/application/store/toast/toastStore";
import { ToastType } from "~~/application/components/Common/Toast/model/ToastType";
import { useI18n } from "vue-i18n";
import { useContestService } from "~~/application/composables/service/useContestService";
import {
  useLearningScreenRecordingService
} from "~~/application/composables/service/useLearningScreenRecordingService";
import { RecorderDomainType } from "~~/package/mapper/recorder/model/RecorderDomainType";
import DialogType from "~~/application/components/Common/Dialog/model/DialogType";
import { useProSkillAssureService } from "~/composables/service/useProSkillAssureService";
import { TaskType } from "~~/package/mapper/learning/model/TaskType";
import {useLearningRunCodeService} from "~/composables/service/useLearningRunCodeService";
import {useLearningDirectoryService} from "~/composables/service/useLearningDirectoryService";

export const useLearningWindowEventService = (domainType = RecorderDomainType.NONE) => {
  const { t } = useI18n({
    useScope: "local"
  });
  const learningStore = useLearningStore();
    const assessmentStore = useLearningAssessmentStore();
  const dialogStore = useDialogStore();
  const toastStore = useToastStore();
  const contestService = useContestService();
  const proSkillAssureService = useProSkillAssureService();
  const learningScreenRecordingService = useLearningScreenRecordingService();
  const multiMonitorIntervalId = ref<number | NodeJS.Timeout>(0);
  const screenshotDimTimeoutId = ref<number | NodeJS.Timeout>(0);
  const isProtectKeyDownFlag = ref(false);
  const isScreenshotDimmedFlag = ref(false);
  const tabFocusIntervalId = ref<number | NodeJS.Timeout>(0);
  const wasWindowFocused = ref(true);
  const isTabSwitchReported = ref(false);
  const learningRunCodeService = useLearningRunCodeService();
  const learningDirectoryService = useLearningDirectoryService();

  const addEventListener = (
    refreshDisable = true,
    isProtectKeyDown = false,
    isProtectCopyPaste = false,
    isProtectTab = false,
    isProtectMultiMonitor = false,
    isScreenshotDimmed = false
  ) => {
    if (refreshDisable) {
      window.addEventListener("beforeunload", openRefreshConfirm);
    }
    isProtectKeyDownFlag.value = isProtectKeyDown;
    isScreenshotDimmedFlag.value = isScreenshotDimmed;
    window.addEventListener("keydown", preventDeveloperToolsAccessKeyDown);
    window.addEventListener("keyup", preventDeveloperToolsAccessKeyUp);
    window.addEventListener("contextmenu", preventDeveloperToolsAccessMouseRightDown);
    if (isProtectCopyPaste) {
      window.addEventListener("copy", preventClipboardEvent);
      window.addEventListener("paste", preventClipboardEvent);
      window.addEventListener("cut", preventClipboardEvent)
    }
    if (isProtectTab) {
      window.addEventListener("blur", preventTabChangeEvent);
      window.addEventListener("focus", onWindowFocusReset);
      startTabFocusPolling();
    }
    if (isProtectMultiMonitor) {
      preventMultiMonitorEvent(true);
    }
    window.addEventListener("keydown", onKeyDownSaveEvent);
  };

  const removeEventListener = (
    refreshDisable = true,
    isProtectKeyDown = false,
    isProtectCopyPaste = false,
    isProtectTab = false,
    isProtectMultiMonitor = false
  ) => {

    if (refreshDisable) {
      window.removeEventListener("beforeunload", openRefreshConfirm);
    }

    window.removeEventListener("beforeunload", openRefreshConfirm);
    window.removeEventListener("keydown", preventDeveloperToolsAccessKeyDown);
    window.removeEventListener("keyup", preventDeveloperToolsAccessKeyUp);
    window.removeEventListener("contextmenu", preventDeveloperToolsAccessMouseRightDown);
    if (isProtectCopyPaste) {
      window.removeEventListener("copy", preventClipboardEvent);
      window.removeEventListener("paste", preventClipboardEvent);
      window.removeEventListener("cut", preventClipboardEvent)
    }
    if (isProtectTab) {
      window.removeEventListener("blur", preventTabChangeEvent);
      window.removeEventListener("focus", onWindowFocusReset);
      stopTabFocusPolling();
    }
    if (isProtectMultiMonitor) {
      preventMultiMonitorEvent(false);
    }
    window.removeEventListener("keydown", onKeyDownSaveEvent);
    clearTimeout(screenshotDimTimeoutId.value as number);
    learningStore.screenshotDimmed = false;
  };

  // PrintScreen 단발 키 대응: keydown/keyup 어느 쪽이 오든 딤을 켜고 짧은 타이머로 자동 해제한다.
  // (Windows Chrome은 PrintScreen에 대해 보통 keyup만 발생시키고 keydown은 OS가 가로채 누락될 수 있음)
  const dimForPrintScreen = () => {
    if (!isScreenshotDimmedFlag.value) return;
    learningStore.screenshotDimmed = true;
    clearTimeout(screenshotDimTimeoutId.value as number);
    screenshotDimTimeoutId.value = setTimeout(() => {
      learningStore.screenshotDimmed = false;
    }, 1000);
  };

  const openRefreshConfirm = (event: BeforeUnloadEvent) => {
    learningStore.isReloadPage = true;
    event.preventDefault();
    event.returnValue = "";
    return false;
  };

  // ASMT-PROCTOR-03: COPY / CUT / PASTE 기록 (팝업 없음) + 화면녹화 클립 저장
  const preventClipboardEvent = async (event: ClipboardEvent) => {
    const clipboardData = event.clipboardData ? event.clipboardData?.getData("Text") : "";
    let actionId = new Date().getTime();

    const cautionType = event.type === "copy" ? CautionType.COPY : event.type === "cut" ? CautionType.CUT : CautionType.PASTE;

    if(domainType === RecorderDomainType.CONTEST){
      actionId = await contestService.setContestUserAction(
        assessmentStore.contestId,
        cautionType,
        clipboardData
      );
    }
    else if(domainType === RecorderDomainType.PRO_SKILL_ASSURE && !assessmentStore.proSkillAssure.myInfo.sampleAssessment){
      actionId = await proSkillAssureService.addProSkillAssureAssessmentSaveCandidateAction(
        assessmentStore.proSkillAssure.assessmentId,
        cautionType,
        clipboardData
      )
    }

    if (assessmentStore.proctoringConfig.useScreenRecording && !assessmentStore.proSkillAssure.myInfo.sampleAssessment)
      learningScreenRecordingService.saveVideo(domainType, actionId);
  };

  const preventTabChangeEvent = async (event: Event | null, isEmbedEvent = false) => {
    setTimeout(async () => {
        // 포커스가 여전히 페이지 안(iframe 편집기 포함 문서)에 있으면 진짜 탭전환이 아니므로 기록하지 않는다.
        // Description ▶(<summary>) 토글·클립보드 복사·Channel Talk 클릭 등 in-page 상호작용을 요소 열거 없이 일괄 커버.
        if (document.hasFocus()) return;

        // 임베드(postMessage) 경로는 독립 채널이라 기존처럼 항상 기록(dedup 미적용).
        // blur 경로는 hasFocus 폴링과 같은 focus-loss 사건을 중복 기록하지 않도록 dedup 적용.
        await reportTabSwitch(!isEmbedEvent);
    }, 0)
  };

  // 탭전환 위반 기록의 실제 본체. blur / hasFocus 폴링 / 임베드 postMessage 세 경로가 공유한다.
  // dedup=true 면 isTabSwitchReported 가드로 이미 기록한 focus-loss 사건의 중복 기록을 막는다.
  // ASMT-PROCTOR-03: TAB_SWITCH 기록 — 최초 경고 팝업, 이후 토스트
  const reportTabSwitch = async (dedup = true) => {
    if (dedup) {
      if (isTabSwitchReported.value) return;
      isTabSwitchReported.value = true;
    }

    let actionId = new Date().getTime();
    if(domainType === RecorderDomainType.CONTEST){
      actionId = await contestService.setContestUserAction(assessmentStore.contestId, CautionType.TAB_SWITCH);
    }
    else if(domainType === RecorderDomainType.PRO_SKILL_ASSURE && !assessmentStore.proSkillAssure.myInfo.sampleAssessment){
      actionId = await proSkillAssureService.addProSkillAssureAssessmentSaveCandidateAction(assessmentStore.proSkillAssure.assessmentId, CautionType.TAB_SWITCH)
    }

    if (assessmentStore.cautionFlag.tabSwitch) {
      assessmentStore.cautionFlag.tabSwitch = false;
      dialogStore.addMultiOption({
        type: DialogType.CautionTabChangePopup
      })
    } else {
      toastStore.openToast({
        toastType: ToastType.WARNING,
        contents: t("skillCertify-caution-tab-toast")
      });
    }

    if (assessmentStore.proctoringConfig.useScreenRecording && !assessmentStore.proSkillAssure.myInfo.sampleAssessment)
      learningScreenRecordingService.saveVideo(domainType, actionId, true);
  };

  // window "blur" 는 포커스가 window 밖으로 나갈 때 한 번만 발생한다. 채널톡 등 cross-origin iframe
  // 을 클릭해 포커스가 iframe 에 물리면 이후 실제 앱 전환(Alt-Tab)에도 blur 가 재발생하지 않아
  // 탭전환을 놓친다. document.hasFocus() 는 "지금 브라우저 창이 OS 포커스를 갖는가"를 매번 새로
  // 반환하므로 이 사각지대를 보강한다. (같은 탭 안 iframe 에 포커스가 있어도 hasFocus 는 true →
  // 내부 iframe 클릭 오탐 없음.)
  const startTabFocusPolling = () => {
    // addEventListener 가 동일 인스턴스에서 재호출될 수 있어(예: 콘테스트/ProSkillAssure의
    // task 전환마다 initAntiCheating 재실행) 기존 인터벌을 먼저 정리해 누적을 막는다.
    clearInterval(tabFocusIntervalId.value as number);
    wasWindowFocused.value = document.hasFocus();
    tabFocusIntervalId.value = setInterval(() => {
      const isFocused = document.hasFocus();
      if (wasWindowFocused.value && !isFocused) {
        // 창이 OS 포커스를 잃은 순간 = 실제 앱 전환. activeElement 예외를 우회해 직접 기록.
        reportTabSwitch(true);
      }
      if (isFocused) isTabSwitchReported.value = false; // 포커스 복귀 시 가드 해제(폴백)
      wasWindowFocused.value = isFocused;
    }, 1000);
  };

  const stopTabFocusPolling = () => {
    clearInterval(tabFocusIntervalId.value as number);
    isTabSwitchReported.value = false;
  };

  // 포커스 복귀 시 가드 즉시 해제(폴링 1초 주기보다 빠른 재전환 대응).
  const onWindowFocusReset = () => {
    isTabSwitchReported.value = false;
  };

  // ASMT-PROCTOR-06: 진행 중 다중 모니터 감지 → Caution 팝업 + MULTI_MONITOR 기록
  const preventMultiMonitorEvent = (isSet: boolean) => {
    if (isSet) {
      multiMonitorIntervalId.value = setInterval(async () => {
        if (window.screen.isExtended as boolean) {
          if (assessmentStore.cautionFlag.multiMonitor) {
            dialogStore.addMultiOption({
              type: DialogType.CautionMultiMonitorPopup,
              props: {
                domainType
              }
            })
            assessmentStore.cautionFlag.multiMonitor = false;
            learningScreenRecordingService.stopRecording();
            if(domainType === RecorderDomainType.CONTEST){
              await contestService.setContestUserAction(assessmentStore.contestId, CautionType.MULTI_MONITOR);
            }
            else if(domainType === RecorderDomainType.PRO_SKILL_ASSURE && !assessmentStore.proSkillAssure.myInfo.sampleAssessment){
              await proSkillAssureService.addProSkillAssureAssessmentSaveCandidateAction(assessmentStore.proSkillAssure.assessmentId, CautionType.MULTI_MONITOR)
            }
          }
        }
      }, 1000);
    } else {
      clearInterval(multiMonitorIntervalId.value);
    }
  };

  const preventDeveloperToolsAccessKeyUp = (event: KeyboardEvent) => {
    if(event.key === "PrintScreen" || event.code === "PrintScreen") {
      event.preventDefault();
      saveKeyDownEvent(event).then();
      dimForPrintScreen();
    }
    if (isScreenshotDimmedFlag.value && learningStore.screenshotDimmed && (event.key === "Meta" || event.key === "Shift")) {
      // keyup 발생 = Shift+Cmd+3 또는 Shift+Cmd 단독 → 4초 fallback 취소 후 1초 타이머로 교체
      clearTimeout(screenshotDimTimeoutId.value as number);
      screenshotDimTimeoutId.value = setTimeout(() => {
        learningStore.screenshotDimmed = false;
      }, 1000);
    }
  }

  // ASMT-PROCTOR-03, ASMT-PROCTOR-13: 비허용 단축키(저장·캡처·소스보기·DevTools, Mac/Win) 차단 → USE_NOT_ALLOWED_SHORTCUT 기록
  const preventDeveloperToolsAccessKeyDown = (event: KeyboardEvent) => {
    if (isScreenshotDimmedFlag.value && ((event.key === "Meta" && event.shiftKey) || (event.key === "Shift" && event.metaKey))) {
      learningStore.screenshotDimmed = true;
      // 5초 fallback: Shift+Cmd+4/5 시 keyup이 오지 않으므로 자동 해제 보장
      clearTimeout(screenshotDimTimeoutId.value as number);
      screenshotDimTimeoutId.value = setTimeout(() => {
        learningStore.screenshotDimmed = false;
      }, 4000);
    }

    if (event.key === "PrintScreen" || event.code === "PrintScreen") {
      dimForPrintScreen();
    }

    if (
        // 단축키 예외 항목 추가
        !(
            // ctrl + shift  + s
            event.ctrlKey && event.shiftKey && event.keyCode === 83 ||
            // ctrl + shift + u
            event.ctrlKey && event.shiftKey && event.keyCode === 85 ||
            // ctrl + alt + u
            event.ctrlKey && event.altKey && event.keyCode === 85
        )
        &&
        (
            // mac screenshot (shift + command)
            event.key === "Meta" && event.shiftKey ||
            event.key === "Shift" && event.metaKey ||
            // command + alt  + i
            event.metaKey && event.altKey && event.keyCode === 73 ||
            // command + alt  + j
            event.metaKey && event.altKey && event.keyCode === 74 ||
            // command + alt  + c
            event.metaKey && event.altKey && event.keyCode === 67 ||
            // command + shift  + c
            event.metaKey && event.shiftKey && event.keyCode === 67 ||
            // command + alt  + u
            event.metaKey && event.altKey && event.keyCode === 85 ||
            // ctrl + shift  + i
            event.ctrlKey && event.shiftKey && event.keyCode === 73 ||
            // ctrl + shift  + j
            event.ctrlKey && event.shiftKey && event.keyCode === 74 ||
            // ctrl + shift  + c
            event.ctrlKey && event.shiftKey && event.keyCode === 67 ||
            // f12
            event.keyCode === 123 ||
            // ctrl + u
            event.ctrlKey && event.keyCode === 85 ||
            // Prtsc
            event.key === "PrintScreen" ||
            // ctrl + s
            event.ctrlKey && event.keyCode === 83 ||
            // command + s
            event.metaKey && event.keyCode === 83 ||
            // ctrl + p
            event.ctrlKey && event.key === 'p' ||
            // command + p
            event.metaKey && event.key === 'p'
        )
    ) {
      event.preventDefault();
      saveKeyDownEvent(event).then();
      return false;
    }
  };

  const saveKeyDownEvent = async (event: KeyboardEvent) => {
    if (!isProtectKeyDownFlag.value) {
      return;
    }

    let keyPressed = "";
    if (event.ctrlKey) keyPressed += "Ctrl";
    if (event.metaKey) keyPressed += "Command";
    if (event.altKey) keyPressed += " + Alt";
    if (event.shiftKey) keyPressed += " + Shift";
    keyPressed += keyPressed === "" ? event.key.toUpperCase() : " + " + event.key.toUpperCase();

    const isCtrlS = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's';
    if (isCtrlS && learningStore.isCodeRelatedType) return;

    if (assessmentStore.cautionFlag.shortKey) {
      assessmentStore.cautionFlag.shortKey = false;
      dialogStore.addMultiOption({
        type: DialogType.CautionShortCutPopup
      })
    } else {
      toastStore.openToast({
        toastType: ToastType.WARNING,
        contents: t("skillCertify-caution-short-cut-toast")
      });
    }

    let actionId = new Date().getTime();
    if(domainType === RecorderDomainType.CONTEST){
      actionId = await contestService.setContestUserAction(assessmentStore.contestId, CautionType.USE_NOT_ALLOWED_SHORTCUT, keyPressed);
    }
    else if(domainType === RecorderDomainType.PRO_SKILL_ASSURE && !assessmentStore.proSkillAssure.myInfo.sampleAssessment){
      actionId = await proSkillAssureService.addProSkillAssureAssessmentSaveCandidateAction(assessmentStore.proSkillAssure.assessmentId, CautionType.USE_NOT_ALLOWED_SHORTCUT, keyPressed)
    }

    if (assessmentStore.proctoringConfig.useScreenRecording && !assessmentStore.proSkillAssure.myInfo.sampleAssessment) {
      learningScreenRecordingService.saveVideo(domainType, actionId);
    }
  }

  const preventDeveloperToolsAccessMouseRightDown = (event: MouseEvent) => {
    event.preventDefault();
  };

  // ASMT-PROCTOR-13: 실습 환경 Ctrl+S / ⌘+S → 코드 저장으로 대체
  const onKeyDownSaveEvent = async (event: KeyboardEvent) => {
    if (!learningStore.isCodeRelatedType) return;

    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();

      if (learningStore.task.type === TaskType.MULTI_FILE_CODE) {
        learningDirectoryService.saveMultipleCode(false, true);
      }
      else {
        learningRunCodeService.saveCodeOnly();
      }
    }
  }

  const setIsProtectKeyDownFlag = (isProtectKeyDown: boolean) => {
    isProtectKeyDownFlag.value = isProtectKeyDown
  }

  return {
    addEventListener,
    removeEventListener,

    preventClipboardEvent,
    preventTabChangeEvent,
    preventDeveloperToolsAccessKeyDown,
    preventDeveloperToolsAccessKeyUp,
    setIsProtectKeyDownFlag
  };
};